import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUploadUrl } from '../utils/config';
import AddToTimelineModal from './AddToTimelineModal';
import { photoAPI } from '../services/api';
import './TimelineView.css';

const TIMELINE_STATE_KEY = 'familyTree.timelineState';

function getInitialState() {
  try {
    const saved = sessionStorage.getItem(TIMELINE_STATE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        selectedMemberIds: parsed.selectedMemberIds || [],
        zoom: parsed.zoom || 1,
        scrollX: parsed.scrollX || 0,
      };
    }
  } catch (e) {
    // Ignore parse errors
  }
  return {
    selectedMemberIds: [],
    zoom: 1,
    scrollX: 0,
  };
}

function TimelineView({
  members,
  relationships,
  memberDegrees,
  focalPersonId,
  currentUser,
}) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const timelineRef = useRef(null);
  const scrollRef = useRef(null);
  const hasInitializedScroll = useRef(false);

  // Load initial state
  const [state, setState] = useState(getInitialState);
  const [modalOpen, setModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [photos, setPhotos] = useState([]);

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(TIMELINE_STATE_KEY, JSON.stringify(state));
  }, [state]);

  // Initialize with current user's member if no selection
  useEffect(() => {
    if (state.selectedMemberIds.length === 0 && members.length > 0) {
      // Find member matching current user
      const userMember = members.find(m => m.user_id === currentUser?.id)
        || members.find(m => m.created_by === currentUser?.id);

      if (userMember && userMember.date_of_birth) {
        setState(prev => ({
          ...prev,
          selectedMemberIds: [userMember.id],
        }));
      }
    }
  }, [members, currentUser, state.selectedMemberIds.length]);

  // Fetch photos for timeline
  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const timelinePhotos = await photoAPI.getTimelinePhotos();
        setPhotos(timelinePhotos);
      } catch (error) {
        console.error('Error fetching timeline photos:', error);
      }
    };

    fetchPhotos();
  }, []);

  // Get selected members in order
  const selectedMembers = useMemo(() => {
    return state.selectedMemberIds
      .map(id => members.find(m => m.id === id))
      .filter(Boolean);
  }, [state.selectedMemberIds, members]);

  // Calculate time bounds
  const { minYear, maxYear, yearSpan } = useMemo(() => {
    const currentYear = new Date().getFullYear();

    if (selectedMembers.length === 0) {
      return { minYear: currentYear - 100, maxYear: currentYear + 50, yearSpan: 150 };
    }

    let oldestBirth = currentYear;

    selectedMembers.forEach(member => {
      if (member.date_of_birth) {
        const birthYear = new Date(member.date_of_birth).getFullYear();
        if (birthYear < oldestBirth) {
          oldestBirth = birthYear;
        }
      }
    });

    const min = oldestBirth - 20;
    // Extend 20 years into the future beyond current year
    const max = currentYear + 20;

    return { minYear: min, maxYear: max, yearSpan: max - min };
  }, [selectedMembers]);

  // Collect all events for vertical tick lines
  const allEvents = useMemo(() => {
    const events = [];

    selectedMembers.forEach(member => {
      // Birth event
      if (member.date_of_birth) {
        events.push({
          date: member.date_of_birth,
          type: 'birth',
          memberId: member.id,
          label: `${member.first_name} born`,
        });
      }

      // Death event
      if (member.date_of_death) {
        events.push({
          date: member.date_of_death,
          type: 'death',
          memberId: member.id,
          label: `${member.first_name} died`,
        });
      }

      // Custom life events
      if (member.events) {
        try {
          const customEvents = typeof member.events === 'string'
            ? JSON.parse(member.events)
            : member.events;

          if (Array.isArray(customEvents)) {
            customEvents.forEach(event => {
              if (event.date) {
                events.push({
                  date: event.date,
                  type: 'custom',
                  memberId: member.id,
                  label: event.name || 'Event',
                });
              }
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    });

    // Add relationship events (marriages)
    const selectedIds = new Set(state.selectedMemberIds);
    relationships.forEach(rel => {
      if (rel.marriage_date &&
        (selectedIds.has(rel.person1_id) || selectedIds.has(rel.person2_id))) {
        const person1 = members.find(m => m.id === rel.person1_id);
        const person2 = members.find(m => m.id === rel.person2_id);
        if (person1 && person2) {
          events.push({
            date: rel.marriage_date,
            type: 'marriage',
            memberId: rel.person1_id,
            label: `${person1.first_name} & ${person2.first_name} married`,
          });
        }
      }
    });

    // Add photo events (for selected members)
    photos.forEach(photo => {
      if (photo.photo_date && photo.tagged_member_ids) {
        // Check if any tagged member is in the selected members list
        const hasSelectedMember = photo.tagged_member_ids.some(id => selectedIds.has(id));
        if (hasSelectedMember) {
          events.push({
            date: photo.photo_date,
            type: 'photo',
            photoId: photo.id,
            label: photo.caption || 'Photo',
            filePath: photo.file_path,
            taggedMemberIds: photo.tagged_member_ids,
          });
        }
      }
    });

    // Sort by date
    events.sort((a, b) => new Date(a.date) - new Date(b.date));

    return events;
  }, [selectedMembers, relationships, members, state.selectedMemberIds, photos]);

  // Calculate year position as percentage
  const getYearPosition = useCallback((year) => {
    return ((year - minYear) / yearSpan) * 100;
  }, [minYear, yearSpan]);

  // Calculate date position as percentage
  const getDatePosition = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const dayOfYear = Math.floor((date - new Date(year, 0, 0)) / (1000 * 60 * 60 * 24));
    const fractionalYear = year + (dayOfYear / 365);
    return ((fractionalYear - minYear) / yearSpan) * 100;
  }, [minYear, yearSpan]);

  // Aggregate events that are close together
  const aggregatedEvents = useMemo(() => {
    if (allEvents.length === 0) return [];

    // Threshold in pixels for clustering (at zoom level 1)
    const CLUSTER_THRESHOLD_PX = 30;
    // Convert to percentage based on timeline width
    const clusterThresholdPercent = (CLUSTER_THRESHOLD_PX / (yearSpan * BASE_PIXELS_PER_YEAR * state.zoom)) * 100;

    const clusters = [];
    let currentCluster = null;

    allEvents.forEach((event) => {
      const eventPos = getDatePosition(event.date);

      if (!currentCluster) {
        // Start new cluster
        currentCluster = {
          events: [event],
          position: eventPos,
          minPos: eventPos,
          maxPos: eventPos,
        };
      } else {
        // Check if event is close enough to current cluster
        if (Math.abs(eventPos - currentCluster.maxPos) <= clusterThresholdPercent) {
          // Add to current cluster
          currentCluster.events.push(event);
          currentCluster.minPos = Math.min(currentCluster.minPos, eventPos);
          currentCluster.maxPos = Math.max(currentCluster.maxPos, eventPos);
          // Update position to center of cluster
          currentCluster.position = (currentCluster.minPos + currentCluster.maxPos) / 2;
        } else {
          // Save current cluster and start new one
          clusters.push(currentCluster);
          currentCluster = {
            events: [event],
            position: eventPos,
            minPos: eventPos,
            maxPos: eventPos,
          };
        }
      }
    });

    // Don't forget the last cluster
    if (currentCluster) {
      clusters.push(currentCluster);
    }

    return clusters;
  }, [allEvents, yearSpan, state.zoom, getDatePosition]);

  // Generate year markers
  const yearMarkers = useMemo(() => {
    const markers = [];
    // Determine step based on year span
    let step;
    if (yearSpan > 200) {
      step = 20;
    } else if (yearSpan > 100) {
      step = 10;
    } else if (yearSpan > 50) {
      step = 5;
    } else {
      step = 1;
    }

    // Start from a round number
    const startYear = Math.floor(minYear / step) * step;

    for (let year = startYear; year <= maxYear + step; year += step) {
      if (year >= minYear - step) {
        markers.push({
          year,
          position: getYearPosition(year),
          isMajor: year % 10 === 0,
        });
      }
    }

    return markers;
  }, [minYear, maxYear, yearSpan, getYearPosition]);

  // Handlers
  const handleAddClick = useCallback(() => {
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleAddMembers = useCallback((memberIds) => {
    setState(prev => ({
      ...prev,
      selectedMemberIds: [...prev.selectedMemberIds, ...memberIds],
    }));
    setModalOpen(false);
  }, []);

  const handleRemoveMember = useCallback((memberId) => {
    setState(prev => ({
      ...prev,
      selectedMemberIds: prev.selectedMemberIds.filter(id => id !== memberId),
    }));
  }, []);

  const handleMemberClick = useCallback((member) => {
    navigate(`/member/${member.id}`);
  }, [navigate]);

  // Pixels per year at zoom level 1
  const BASE_PIXELS_PER_YEAR = 20;

  // Calculate total timeline width in pixels
  const timelineWidth = useMemo(() => {
    return yearSpan * BASE_PIXELS_PER_YEAR * state.zoom;
  }, [yearSpan, state.zoom]);

  // Set initial scroll position to show "today - 50 years" to "today"
  useEffect(() => {
    if (hasInitializedScroll.current || !scrollRef.current) return;

    const currentYear = new Date().getFullYear();
    const targetStartYear = currentYear - 50;

    // Calculate the scroll position to show targetStartYear at the left edge
    const yearsFromMin = targetStartYear - minYear;
    const scrollPosition = Math.max(0, yearsFromMin * BASE_PIXELS_PER_YEAR * state.zoom);

    scrollRef.current.scrollLeft = scrollPosition;
    hasInitializedScroll.current = true;
  }, [minYear, state.zoom]);

  const handleZoomIn = useCallback(() => {
    setState(prev => ({
      ...prev,
      zoom: Math.min(prev.zoom * 1.5, 10),
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setState(prev => ({
      ...prev,
      zoom: Math.max(prev.zoom / 1.5, 0.25),
    }));
  }, []);

  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    }
  }, [handleZoomIn, handleZoomOut]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setState(prev => {
      const newOrder = [...prev.selectedMemberIds];
      const [removed] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, removed);
      return { ...prev, selectedMemberIds: newOrder };
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Helper functions
  const getInitials = (member) => {
    return `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
  };

  const getAvatarColor = (member) => {
    const hash = (member.first_name + member.last_name).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `hsl(${hash % 360}, 45%, 65%)`;
  };

  const getLifespanStyle = (member) => {
    // Check for valid birth date
    if (!member.date_of_birth || member.date_of_birth.trim() === '') {
      return null;
    }

    const birthDate = new Date(member.date_of_birth);
    if (isNaN(birthDate.getTime())) {
      return null; // Invalid date
    }

    const birthPos = getDatePosition(member.date_of_birth);
    const currentYear = new Date().getFullYear();
    const birthYear = birthDate.getFullYear();
    const color = getAvatarColor(member);

    // Check for valid death date
    if (member.date_of_death && member.date_of_death.trim() !== '') {
      const deathDate = new Date(member.date_of_death);
      if (!isNaN(deathDate.getTime())) {
        // Known death date - solid bar
        const deathPos = getDatePosition(member.date_of_death);
        return {
          left: `${birthPos}%`,
          width: `${Math.max(0, deathPos - birthPos)}%`,
          background: color,
        };
      }
    }

    // Unknown or invalid death - fade out over 10 years from "now"
    const yearsAlive = currentYear - birthYear;
    const currentPos = getYearPosition(currentYear);

    if (yearsAlive > 10) {
      // Person was born more than 10 years ago - show solid bar to now with fade at end
      return {
        left: `${birthPos}%`,
        width: `${Math.max(0, currentPos - birthPos)}%`,
        background: `linear-gradient(to right, ${color} 0%, ${color} 90%, transparent 100%)`,
      };
    } else {
      // Person born recently - fade over remaining years
      const fadeEndYear = Math.min(birthYear + 10, currentYear);
      const fadeEndPos = getYearPosition(fadeEndYear);
      return {
        left: `${birthPos}%`,
        width: `${Math.max(0, fadeEndPos - birthPos)}%`,
        background: `linear-gradient(to right, ${color}, transparent)`,
      };
    }
  };

  const selectedIdsSet = useMemo(() => new Set(state.selectedMemberIds), [state.selectedMemberIds]);

  return (
    <div
      className="timeline-view"
      ref={containerRef}
      onWheel={handleWheel}
    >
      <div className="timeline-toolbar">
        <div className="toolbar-group" />
        <div className="zoom-controls">
          <button onClick={handleZoomOut} className="zoom-btn" title="Zoom out (less detail)">−</button>
          <span className="zoom-level" title={`${Math.round(BASE_PIXELS_PER_YEAR * state.zoom)}px/year`}>
            {Math.round(state.zoom * 100)}%
          </span>
          <button onClick={handleZoomIn} className="zoom-btn" title="Zoom in (more detail)">+</button>
        </div>
      </div>

      <div className="timeline-container" ref={timelineRef}>
        {/* Fixed labels column */}
        <div className="timeline-labels">
          {/* Axis label spacer */}
          <div className="axis-label-spacer" />

          {/* Lane labels */}
          {selectedMembers.map((member, index) => (
            <div
              key={member.id}
              className={`lane-label ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <span className="drag-handle" title="Drag to reorder">⠿</span>
              <div
                className="lane-member-info"
                onClick={() => handleMemberClick(member)}
              >
                {member.photo_url ? (
                  <img
                    src={getUploadUrl(member.photo_url)}
                    alt=""
                    className="lane-avatar"
                  />
                ) : (
                  <div
                    className="lane-avatar lane-avatar-initials"
                    style={{ backgroundColor: getAvatarColor(member) }}
                  >
                    {getInitials(member)}
                  </div>
                )}
                <span className="lane-name">
                  {member.first_name} {member.last_name || ''}
                </span>
              </div>
              <button
                className="lane-remove-btn"
                onClick={() => handleRemoveMember(member.id)}
                title="Remove from timeline"
              >
                ×
              </button>
            </div>
          ))}

          {/* Add person button */}
          <div className="lane-label add-lane">
            <button
              className="add-person-btn"
              onClick={handleAddClick}
              title="Add person to timeline"
            >
              + Add Person
            </button>
          </div>
        </div>

        {/* Scrollable timeline content */}
        <div className="timeline-scroll" ref={scrollRef}>
          {/* Year axis */}
          <div
            className="timeline-axis"
            style={{ width: `${timelineWidth}px`, minWidth: `${timelineWidth}px` }}
          >
            {yearMarkers.map(marker => (
              <div
                key={marker.year}
                className={`year-marker ${marker.isMajor ? 'major' : 'minor'}`}
                style={{ left: `${marker.position}%` }}
              >
                <span className="year-label">{marker.year}</span>
              </div>
            ))}
          </div>

          {/* Lanes content */}
          <div className="timeline-lanes">
            {selectedMembers.map((member, index) => {
              const lifespanStyle = getLifespanStyle(member);

              return (
                <div
                  key={member.id}
                  className={`lane-content ${draggedIndex === index ? 'dragging' : ''}`}
                  style={{ width: `${timelineWidth}px`, minWidth: `${timelineWidth}px` }}
                >
                  {lifespanStyle ? (
                    <div className="lifespan-bar" style={lifespanStyle} />
                  ) : (
                    <div className="lifespan-unknown">No dates</div>
                  )}
                </div>
              );
            })}

            {/* Add person lane placeholder */}
            <div
              className="lane-content add-lane"
              style={{ width: `${timelineWidth}px`, minWidth: `${timelineWidth}px` }}
            />
          </div>

          {/* Event tick lines with clustering */}
          <div
            className="timeline-events"
            style={{ width: `${timelineWidth}px`, minWidth: `${timelineWidth}px` }}
          >
            {aggregatedEvents.map((cluster, index) => {
              const isCluster = cluster.events.length > 1;
              const hasPhoto = cluster.events.some(e => e.type === 'photo');

              // Build tooltip
              const tooltip = cluster.events.map(e => `${e.label} (${e.date})`).join('\n');

              return (
                <div
                  key={`cluster-${index}`}
                  className={`event-tick ${cluster.events[0].type} ${isCluster ? 'cluster' : ''} ${hasPhoto ? 'has-photo' : ''}`}
                  style={{ left: `${cluster.position}%` }}
                  title={tooltip}
                >
                  {isCluster && (
                    <span className="event-count">{cluster.events.length}</span>
                  )}
                  {hasPhoto && <span className="photo-icon">📷</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {modalOpen && (
        <AddToTimelineModal
          members={members}
          memberDegrees={memberDegrees}
          focalPersonId={focalPersonId}
          selectedIds={selectedIdsSet}
          onAdd={handleAddMembers}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

export default TimelineView;
